import { Link } from 'wouter';
import type { ReactNode } from 'react';

interface ButtonProps {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
  variant?: 'primary' | 'brand' | 'secondary' | 'inverse' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

/**
 * Polymorphic button: renders a wouter <Link> for internal paths, an <a> for
 * external URLs, and a <button> otherwise.
 *
 * Every size clears the 44px minimum touch target (WCAG 2.5.5), so the same
 * component is safe on mobile without a separate variant.
 */
export function Button({
  children,
  href,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled = false,
  className = '',
}: ButtonProps) {
  // `group` so a caller can animate an icon inside the label on hover.
  const base =
    'group inline-flex items-center justify-center gap-2 rounded-full font-medium transition-all duration-500 ease-brand disabled:opacity-50 disabled:pointer-events-none';

  const variants = {
    /*
      primary is the accent, not the brand blue: one warm colour marks every
      action on the site, so a visitor learns it once. Text is `ink`, not
      white — navy on this amber is 7.74:1, white on it is 2.04:1.
    */
    primary: 'bg-accent text-ink hover:bg-accent-deep',
    /** The old blue fill, kept for actions that must not compete with a CTA. */
    brand: 'bg-brand text-white hover:bg-brand-deep',
    secondary: 'border border-ink/20 text-ink hover:border-ink hover:bg-ink hover:text-white',
    inverse: 'bg-white text-ink hover:bg-accent hover:text-ink',
    ghost: 'text-current hover:opacity-70',
  } as const;

  // min-h-11 == 44px: the accessible minimum touch target.
  const sizes = {
    sm: 'min-h-11 px-5 text-sm',
    md: 'min-h-12 px-7 text-base',
    lg: 'min-h-14 px-9 text-base',
  } as const;

  const cls = `${base} ${variants[variant]} ${sizes[size]} ${className}`;

  if (href) {
    const external = href.startsWith('http') || href.startsWith('mailto:');
    if (external) {
      return (
        <a
          href={href}
          className={cls}
          target={href.startsWith('http') ? '_blank' : undefined}
          rel={href.startsWith('http') ? 'noreferrer noopener' : undefined}
        >
          {children}
        </a>
      );
    }
    return (
      <Link href={href} className={cls}>
        {children}
      </Link>
    );
  }

  return (
    <button type={type} onClick={onClick} disabled={disabled} className={cls}>
      {children}
    </button>
  );
}

/**
 * The circular arrow button from the client's banner — a real brand element,
 * reused rather than reinvented.
 */
export function ArrowCircleButton({
  href,
  label,
  className = '',
}: {
  href: string;
  label: string;
  className?: string;
}) {
  const content = (
    <>
      <span className="sr-only">{label}</span>
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5 transition-transform duration-500 ease-brand group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M7 17 L17 7" />
        <path d="M8 7 h9 v9" />
      </svg>
    </>
  );

  const cls = `group inline-grid h-14 w-14 place-items-center rounded-full bg-brand text-white transition-colors duration-500 ease-brand hover:bg-brand-deep ${className}`;

  if (href.startsWith('http') || href.startsWith('mailto:')) {
    return (
      <a href={href} className={cls}>
        {content}
      </a>
    );
  }
  return (
    <Link href={href} className={cls}>
      {content}
    </Link>
  );
}
